import { PanelFooter } from '@Components/PanelFooter';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import type { ApiStorageTransaction } from '@Model';
import { Button, FlexColumn, type IEditFrame } from '@synqo/frontend-component-library';
import { useState } from 'react';
import { useOpenRelatedBooking } from '../useOpenRelatedBooking';
import { BookingHeader } from './BookingHeader';
import { bookingState, canCancel, showsOrderNotice, showsStockEffect } from './cancellation';
import { StorageTransactionCancelSection } from './Cancel/StorageTransactionCancelSection';
import { OrderBookingNotice } from './OrderBookingNotice';
import { StorageTransactionViewSection } from './Sections';
import { StockPreviewLine } from './StockPreviewLine';
import classes from './StorageTransactionViewBody.module.css';

interface IProps {
    frame: IEditFrame<ApiStorageTransaction>;
    /** Called after the booking shown was cancelled. */
    onCancelled?: () => void;
}

// @ai-generated 2026-10-23T17:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function StorageTransactionViewBody({ frame, onCancelled }: IProps) {
    const booking = frame.data;
    const hasPermission = useHasPermission();
    const openRelated = useOpenRelatedBooking();
    const [cancelOpen, setCancelOpen] = useState(false);
    const state = bookingState(booking);

    const cancelled = async () => {
        setCancelOpen(false);
        await frame.refetchData();
        onCancelled?.();
    };
    const mayCancel = canCancel(booking, hasPermission(Permission.AuftragsbearbeitungStorageTransactionsUpdate));

    return (
        <FlexColumn gap="none" align="stretch">
            <BookingHeader booking={booking} onClose={frame.events.onClose} />
            <FlexColumn className={classes.content} align="stretch">
                {showsOrderNotice(booking) && <OrderBookingNotice booking={booking} state={state} />}
                <StorageTransactionViewSection
                    booking={booking}
                    state={state}
                    onOpenRelated={() => void openRelated(booking, { onCancelled })}
                />
                {showsStockEffect(booking) && !cancelOpen && (
                    <StockPreviewLine
                        storageLocationId={booking.storage_location.foreign_key}
                        materialId={booking.material.foreign_key}
                        avvId={booking.avv.foreign_key}
                        occurredAt={booking.occurred_at}
                        type={booking.type}
                        quantity={booking.quantity}
                        bookingId={booking.id}
                        view="effect"
                    />
                )}
            </FlexColumn>
            {cancelOpen && (
                <PanelFooter>
                    <StorageTransactionCancelSection
                        booking={booking}
                        onClose={() => setCancelOpen(false)}
                        onCancelled={cancelled}
                    />
                </PanelFooter>
            )}
            {!cancelOpen && mayCancel && (
                <PanelFooter>
                    <Button appearance="outlined" onClick={() => setCancelOpen(true)}>
                        Stornieren …
                    </Button>
                </PanelFooter>
            )}
        </FlexColumn>
    );
}
